import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PackageCheck } from "lucide-react";
import { useState } from "react";
import type { LigneBonLivraison } from "@shared/schema";

const STATUT_LABELS: Record<string, string> = {
  a_preparer: "À préparer",
  en_preparation: "En préparation",
  prete: "Prête",
  livree_partielle: "Livrée partiellement",
  livree: "Livrée",
  a_facturer: "À facturer",
  cloturee: "Clôturée",
};

const STATUT_CLASSES: Record<string, string> = {
  a_preparer: "bg-secondary text-secondary-foreground",
  en_preparation: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  prete: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  livree_partielle: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  livree: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  a_facturer: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  cloturee: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
};

interface BonLivraisonRow {
  id: string;
  numeroBl: string;
  statut: string;
  transporteur: string | null;
  datePassageTransporteur: string | null;
  createdAt: string;
  lignes: LigneBonLivraison[];
  devisNumero: string | null;
  devisSociete: string | null;
  devisClientFinalNom: string | null;
  clientNom: string | null;
  resellerNom: string | null;
}

export default function BonsLivraisonList() {
  const [, navigate] = useLocation();
  const [statutFilter, setStatutFilter] = useState<string>("all");

  const { data: rows = [], isLoading } = useQuery<BonLivraisonRow[]>({
    queryKey: ["/api/bons-livraison", statutFilter],
    queryFn: async () => {
      const params = statutFilter !== "all" ? `?statut=${statutFilter}` : "";
      const res = await authFetch(`/api/bons-livraison${params}`);
      if (!res.ok) throw new Error("Erreur de chargement");
      return res.json();
    },
  });

  const clientAffiche = (r: BonLivraisonRow) =>
    r.clientNom || r.resellerNom || r.devisClientFinalNom || r.devisSociete || "—";

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2" data-testid="text-page-title">
            <PackageCheck className="w-5 h-5 text-primary" /> Préparation de commande
          </h1>
          <p className="text-sm text-muted-foreground">Suivi des bons de livraison à préparer et expédier</p>
        </div>
        <Select value={statutFilter} onValueChange={setStatutFilter}>
          <SelectTrigger className="w-[200px]" data-testid="select-filter-statut">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUT_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Numéro</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Transporteur</TableHead>
              <TableHead className="text-right">Nb lignes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Chargement…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8" data-testid="text-empty-bl">
                  Aucun bon de livraison.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/bons-livraison/${r.id}`)}
                  data-testid={`row-bl-${r.id}`}
                >
                  <TableCell className="font-medium" data-testid={`text-numero-${r.id}`}>{r.numeroBl}</TableCell>
                  <TableCell data-testid={`text-client-${r.id}`}>{clientAffiche(r)}</TableCell>
                  <TableCell data-testid={`text-date-${r.id}`}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString("fr-FR") : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUT_CLASSES[r.statut] || ""} data-testid={`badge-statut-${r.id}`}>
                      {STATUT_LABELS[r.statut] || r.statut}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`text-transporteur-${r.id}`}>{r.transporteur || "—"}</TableCell>
                  <TableCell className="text-right" data-testid={`text-nb-lignes-${r.id}`}>
                    {Array.isArray(r.lignes) ? r.lignes.length : 0}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
